import Immutable from 'seamless-immutable';
import RefData from '../../ref-data';

/**
 * Collection
 * @typedef {Object} Collection
 * @property {string} id - the collection id
 * @property {string} name - the collection name
 * @property {string} modifiedBy - name/email of last person to update the record
 * @property {string} lastUpdated - datetime audit string for last time record was updated
 * @property {object} customOrderStrategy - if populated, specifies how to custom order content in the collection RefData.Enum.STRATEGY.CUSTOMORDER.TOP/BOTTOM
 */

/**
 * Create a CollectionModel from provided data
 */
function create(data = {}) {
  const result = _.clone(data);
  result.customOrderStrategy = !!data.customOrderStrategy ? RefData.Model.hydrate(data.customOrderStrategy) : null;
  return Immutable(result, { prototype: model });
}

/**
 * Transforms a backend collection object into a frontend Collection Model
 * @param {Object} collection - backend formatted collection
 * @returns {Collection}
 */
function deserialize(collection) {
  const result = _.pick(collection, _keys);

  // timestamp record of last edit for dirty check
  result.editedTimestamp = new Date(result.lastUpdated).getTime();

  return create(result);
}

/**
 * Transform a CollectionModel into a backend collection object
 * @returns {Object}
 */
function serialize(collection) {
  const result = collection.asMutable({ deep: true });
  result.customOrderStrategy = !!collection.customOrderStrategy ? collection.customOrderStrategy.id : null;
  delete result.editedTimestamp;

  return _.pick(result, _keys);
}

/** The CollectionModel prototype */
const model = {
  get isNew() {
    return this.id === 'new';
  },
  get isDirty() {
    return !this.lastUpdated || this.editedTimestamp > new Date(this.lastUpdated);
  },
  get isNewAndUnamed() {
    return this.isNew && !this.name;
  },
  get hasCampaign() {
    return !!this.containerRef && this.containerRef.indexOf('campaign') !== -1;
  },
};

/**
 * An array of keys on the API Collection object that should be shared between the API requests and responses
 */
const _keys = ['id', 'name', 'modifiedBy', 'lastUpdated', 'customOrderStrategy', 'containerRef'];

export default {
  create,
  deserialize,
  serialize,
};
